/**
 * @fileoverview Capabilities page.
 * @description Super Admin's screen for what each Sales role may do: a Sales / Banking tab strip
 * (Banking is "coming soon"), and for Sales a card per module listing its permissions down the side
 * and the roles across the top, each cell a green Yes or red No. "Edit capabilities" turns the
 * Yes/No cells of the editable roles into toggles (Super Admin's column is fixed and an inactive
 * permission can't be granted), highlights what has changed, and "Save changes" sends only the
 * differences. The "Sales app" filter hides permissions that control nothing in
 * this app. The matrix is read from the real authorization endpoints (composed in
 * features/capabilities/services/role-capabilities.service.ts) and Save sends one bulk PATCH; with
 * VITE_ENABLE_MOCK_API=true the feature's mock adapter serves the same endpoints. While editing, the
 * table shows the matrix as it was when editing started (see useCapabilitiesEditor). Only a failed
 * first load replaces the page with an error: if a later refresh fails (say, right after a failed
 * save) the table, the draft and the Save button stay, with a note and a retry.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  BankingCapabilitiesPlaceholder,
  CapabilitiesActions,
  CapabilitiesLegend,
  CapabilitiesNotices,
  CapabilitiesSkeleton,
  CapabilitiesTabs,
  CapabilityGroupCard,
  filterGroups,
  useCapabilitiesEditor,
} from '@/features/capabilities'
import PageHeader from '@/shared/components/molecules/PageHeader'

import type { CapabilitiesTab, CapabilityFilter } from '@/features/capabilities'

/**
 * @component
 */
function CapabilitiesPage() {
  const { t } = useTranslation()
  const editor = useCapabilitiesEditor()
  const { matrix, matrixQuery } = editor
  const [tab, setTab] = useState<CapabilitiesTab>('sales')
  const [filter, setFilter] = useState<CapabilityFilter>('used')

  const onSalesTab = tab === 'sales'
  let description = t('capabilities.descriptionBanking')
  if (onSalesTab) {
    description = editor.editing
      ? t('capabilities.descriptionEditing')
      : t('capabilities.description')
  }

  const header = (
    <PageHeader
      title={t('nav.capabilities')}
      description={description}
      filters={
        onSalesTab && matrix !== undefined ? (
          <CapabilitiesActions
            editing={editor.editing}
            changeCount={editor.changes.length}
            saveBlocked={editor.limitWarning !== null}
            isSaving={editor.isSaving}
            onEdit={editor.startEditing}
            onCancel={editor.cancelEditing}
            onSave={editor.save}
          />
        ) : undefined
      }
    />
  )

  // No matrix yet: still loading, or the first load failed. A failed refresh keeps the matrix (and
  // the draft over it), so it is shown below as a note instead of replacing the page.
  if (matrix === undefined) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        {matrixQuery.isError ? (
          <p role="alert" className="text-sm text-danger" data-testid="capabilities-load-error">
            {t('capabilities.loadError')}
          </p>
        ) : (
          <CapabilitiesSkeleton />
        )}
      </div>
    )
  }

  const groups = filterGroups(matrix.groups, filter === 'all')

  return (
    <div className="flex grow flex-col gap-4">
      {header}

      <CapabilitiesNotices
        flash={editor.flash}
        onDismissFlash={editor.dismissFlash}
        saveError={editor.saveError}
        limitWarning={editor.limitWarning}
        refreshFailed={matrixQuery.isError}
        onRetryRefresh={() => void matrixQuery.refetch()}
      />

      <CapabilitiesTabs value={tab} onChange={setTab} locked={editor.editing} />

      {onSalesTab ? (
        <>
          <CapabilitiesLegend
            version={matrix.version}
            customCount={matrix.counts.custom}
            totalPermissions={matrix.counts.permissions}
            filter={filter}
            onFilterChange={setFilter}
          />

          {groups.length > 0 ? (
            groups.map((group) => (
              <CapabilityGroupCard
                key={group.key}
                group={group}
                roles={matrix.roles}
                draft={editor.draft}
                onToggle={editor.toggle}
              />
            ))
          ) : (
            <p
              className="rounded-[10px] border border-divider bg-white px-5 py-11 text-center text-sm text-slate"
              data-testid="capabilities-empty"
            >
              {t('capabilities.empty')}
            </p>
          )}
        </>
      ) : (
        <BankingCapabilitiesPlaceholder />
      )}
    </div>
  )
}

export default CapabilitiesPage
